import { useState } from "react";
import { Link } from "react-router-dom";
import type { RunOverrides } from "@zenforge/shared";
import { api, useApi } from "../api.js";
import { Notice, useToast } from "../components.js";
import {
  CollectionSummary,
  KnowledgeNote,
  SyllabusSelector,
  TargetTree,
  useGenerationTargets,
  type KnowledgeSourceRow,
} from "./generation-targets.js";
import { PdfViewerModal } from "../PdfViewerModal.js";
import { RunPipelineModal } from "./RunPipelineModal.js";

interface PipelineRow {
  id: number;
  name: string;
  description: string | null;
  defaultModel?: string | null;
  steps: { id: number }[];
}

/**
 * Run Pipeline — queues multi-step pipeline executions over the same targets
 * the category screen generates against.
 *
 * Split out of Content Generation, where it was a mode toggle on a shared
 * page. The two flows produce different things (pipeline executions versus
 * category jobs) and are watched on different screens afterwards, so a toggle
 * meant the buttons, the right-hand panel and half the empty states all had to
 * be read twice to work out which mode you were looking at. Only the target
 * picker is genuinely common, and that is imported.
 */
export function RunPipeline() {
  const t = useGenerationTargets();
  const [selectedPipelineId, setSelectedPipelineId] = useState<number | null>(null);
  // The confirmation step. Targets are chosen on the page; everything else is
  // settled in the modal, so nothing is queued until it is explicitly run.
  const [confirming, setConfirming] = useState(false);
  const [viewingPdf, setViewingPdf] = useState<KnowledgeSourceRow | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const { toast, element: toastElement } = useToast();

  const pipelines = useApi<PipelineRow[]>(
    // activeOnly: a deactivated pipeline must not be offerable to run.
    t.collectionId ? `/pipelines/collection/${t.collectionId}?activeOnly=true` : null,
    [t.collectionId],
  );

  const models = useApi<string[]>("/models");

  const selectedPipeline = pipelines.data?.find((p) => p.id === selectedPipelineId);

  /** Validates the selection, then hands off to the confirmation modal. */
  function openRunModal() {
    if (!t.collectionId) {
      toast("Choose a collection first");
      return;
    }
    if (!selectedPipelineId) {
      toast("Select a pipeline to run");
      return;
    }
    if (t.selectedTopics.size === 0 && t.selectedSubChapters.size === 0) {
      toast("Select at least one topic or subchapter target");
      return;
    }
    setConfirming(true);
  }

  async function runPipeline(overrides: RunOverrides) {
    if (!t.collectionId || !selectedPipelineId) return;
    setSubmitting(true);
    try {
      const result = await api.post<{ created: number }>(
        `/pipelines/collection/${t.collectionId}/run/${selectedPipelineId}`,
        {
          topicIds: [...t.selectedTopics],
          subChapterIds: [...t.selectedSubChapters],
          overrides,
        },
      );
      toast(`Queued ${result.created} pipeline execution${result.created === 1 ? "" : "s"}`);
      t.setSelectedTopics(new Set());
      t.setSelectedSubChapters(new Set());
      setConfirming(false);
      t.refresh();
    } catch (error: unknown) {
      toast(error instanceof Error ? error.message : "Failed to run pipeline");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <>
      <div className="header">
        <p className="muted">
          Execute multi-step generative pipelines across chapters, subchapters, or topics.
        </p>
      </div>

      {t.apiError && (
        <Notice kind="error">
          <strong>Cannot load data:</strong> {t.apiError}
        </Notice>
      )}

      <div className="grid targets-sticky">
        <SyllabusSelector t={t} />
        <TargetTree t={t} onViewPdf={setViewingPdf} />

        <div className="card span-5">
          <h2>Run pipeline</h2>

          <CollectionSummary t={t} />

          {t.collectionId && (
            <div className="field">
              <label>Pipeline</label>
              {pipelines.loading && <p className="muted mini">Loading pipelines…</p>}
              {pipelines.data?.length === 0 && (
                <Notice kind="warn">
                  No active pipelines configured for this collection yet. Configure one in Collection
                  Settings.
                </Notice>
              )}
              {pipelines.data && pipelines.data.length > 0 && (
                <select
                  value={selectedPipelineId ?? ""}
                  onChange={(e) =>
                    setSelectedPipelineId(e.target.value ? Number(e.target.value) : null)
                  }
                >
                  <option value="">Choose a pipeline…</option>
                  {pipelines.data.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} ({p.steps?.length ?? 0} step{p.steps?.length === 1 ? "" : "s"}){p.defaultModel ? ` · 🤖 ${p.defaultModel}` : ""}
                    </option>
                  ))}
                </select>
              )}
              {selectedPipeline && (
                <div className="mini muted" style={{ marginTop: 6, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                  <span>{selectedPipeline.description || "Multi-step Pipeline execution"}</span>
                  {selectedPipeline.defaultModel && (
                    <span className="badge pending mini">🤖 {selectedPipeline.defaultModel}</span>
                  )}
                </div>
              )}
            </div>
          )}

          <KnowledgeNote t={t} />

          <div className="row between" style={{ marginTop: 12 }}>
            <span className="muted">
              {t.targetCount} target{t.targetCount === 1 ? "" : "s"} selected
            </span>
          </div>

          <div className="row" style={{ marginTop: 12, gap: 8, flexWrap: "wrap" }}>
            <button
              type="button"
              className="btn primary"
              onClick={openRunModal}
              disabled={t.targetCount === 0 || !t.collectionId || !selectedPipelineId || submitting}
            >
              Run Pipeline…
            </button>
          </div>
          <p className="muted" style={{ marginTop: 8 }}>
            Runs appear on <Link to="/pipelines">Pipeline Executions</Link>, where each step can be
            inspected, retried or approved.
          </p>
        </div>
      </div>

      {confirming && t.collectionId && selectedPipelineId && (
        <RunPipelineModal
          collectionId={t.collectionId}
          pipelineId={selectedPipelineId}
          pipelineName={selectedPipeline?.name ?? "pipeline"}
          targetCount={t.targetCount}
          models={models.data ?? []}
          submitting={submitting}
          onRun={(overrides) => void runPipeline(overrides)}
          onClose={() => setConfirming(false)}
        />
      )}

      {viewingPdf && t.collectionId && (
        <PdfViewerModal
          source={{
            id: viewingPdf.id,
            collectionId: t.collectionId,
            filename: viewingPdf.filename,
            roleTag: viewingPdf.roleTag,
          }}
          onClose={() => setViewingPdf(null)}
        />
      )}

      {toastElement}
    </>
  );
}
